import 'regenerator-runtime/runtime';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isAngebotsIdEqual,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSpartePayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  addVorbelegteVorschaedenToTaasRequestModel,
  App,
  getTarifbereich,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForShop,
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getSelectedProduktlinie,
  getTaasVertragsoptionen,
} from '@ps20-frontend/preis-lib';
import { Dialogschritt, TaasRequestModel, TaasThhHundData } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { ThhHundActions } from '../thhHundActions';
import { IAppState } from '../appState';
import {
  buildTaasThhHundDataForShop,
  buildTaasThhHundDataForStrecke,
} from './transformer/thhHundData.transformer';

export function* watchQuoteDB() {
  yield takeLatest(GlobalActions.QUOTEDB_DATA_REQUESTED, safeSaga(getQuoteDbData, App.THH_HUND));
  yield takeLatest(ThhHundActions.QUOTEDB_REQUESTED, safeSaga(quoteDbInit, App.THH_HUND));
}

export function* getQuoteDbData(action: GlobalAction<QuoteDbRequestPayload>) {
  const angebotsIds: AngebotsId[] = action.payload.angebotsIds;
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  let taasRequestModel: TaasRequestModel<TaasThhHundData>;
  let selectedProduktlinie: Produktlinie;
  let alleProduktlinien: Array<Produktlinie>;
  const spartenAngebotsId: AngebotsId = { appId: App.THH_HUND };
  if (angebotsIds && !angebotsIds.some(angebot => isAngebotsIdEqual(spartenAngebotsId, angebot))) {
    yield cancel();
    return;
  }

  if (
    (state.produkt && state.gesamtbeitrag !== 0) ||
    state.transientState.fehlerCodeTaas ||
    (state.vertragsoptionen?.beibehalten && state.gesamtbeitragMitRabatt !== 0)
  ) {
    selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    alleProduktlinien = state.produkt.produktlinien;
    if (action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
      taasRequestModel = yield call(buildTaasRequestModel, state);
    } else {
      taasRequestModel = yield call(buildTaasRequestModelShop, state);
    }
    taasRequestModel = addVorbelegteVorschaedenToTaasRequestModel(
      taasRequestModel,
      state.transientState.vorvertragVorschaeden,
      'anzVorschaedenHund',
    );
  }

  const quoteDbPayload: QuoteDbSpartePayload<TaasThhHundData> = {
    angebotsId: { appId: App.THH_HUND },
    taasSparteRequestModel: taasRequestModel,
    selectedProduktlinie,
    alleProduktlinien,
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    gesamtpreis:
      action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN
        ? state.gesamtbeitrag
        : state.gesamtbeitragMitRabatt,
    vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
    antragsnummer: state.antragsnummer,
  };

  if (state.taasInfo.deltafaktoren) {
    quoteDbPayload.sparteErsatzInfo = {
      deltafaktoren: state.taasInfo.deltafaktoren.deltafaktoren,
      kuendigungLiegtVor: state.vertragsoptionen.vorvertragGekuendigt,
    };
  }

  yield call(globalPut.put, new GlobalActions().quoteDBDataSparteGenerated(quoteDbPayload));
}

export function* quoteDbInit() {
  const globalPut = GlobalPut.getInstance();
  yield take(GlobalActions.GESAMTPREIS_CHANGED);
  const state: IAppState = yield select();
  const fehlerCodeTaas = state.transientState.fehlerCodeTaas;
  if (fehlerCodeTaas === null) {
    yield cancel();
    return;
  }
  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: { appId: App.THH_HUND },
    dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
    fehlerCodeTaas,
  };
  yield call([globalPut, globalPut.put], new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

export function buildTaasRequestModel(state: IAppState): TaasRequestModel<TaasThhHundData> {
  const taasData: TaasThhHundData = buildTaasThhHundDataForStrecke(state);
  return buildTaasRequestModelForStrecke(
    getTaasVertragsoptionen(state.vertragsoptionen),
    taasData,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function buildTaasRequestModelShop(state: IAppState): TaasRequestModel<TaasThhHundData> {
  const thhHundData: TaasThhHundData = buildTaasThhHundDataForShop(state);

  const newHashedTaasRequest = createHashedTaasData(thhHundData);
  return buildTaasRequestModelForShop(
    thhHundData,
    newHashedTaasRequest,
    getTaasVertragsoptionen(state.vertragsoptionen),
    state.taasInfo,
    getTarifbereich(state.versicherungsnehmer),
  );
}
